import { ApiClient } from 'src/api/client/ApiClient'
import { apiOps } from 'src/api/defs/apiOps'
import { ClientUser } from 'src/api/defs/ClientUser.type'
import { MyContract } from 'src/api/defs/MyContract.type'
import { MyRent } from 'src/api/defs/MyRent.type'
import { DownloadLink } from 'src/api/defs/DownloadLink.type'
import { ClientReferralInfo } from 'src/api/defs/ClientReferralInfo.type'
import { MyContractFilter as ApiMyContractFilter } from 'src/api/defs/MyContractFilter.type'
import { AllNullable } from 'src/api/AllNullable'
import { MyRentFilter as ApiMyRentFilter } from 'src/api/defs/MyRentFilter.type'
import { MyContractFirstAt } from '../../api/defs/MyContractFirstAt.type'
import { SetLocaleCmd } from 'src/api/defs/SetLocaleCmd.type'
import { CaptureUrlResponse } from '../../api/defs/CaptureUrlResponse.type'
import { AddBoxSharingCmd } from 'src/api/defs/AddBoxSharingCmd.type'
import { MySharedBox } from '../../api/defs/MySharedBox.type'
import { RemoveBoxSharingCmd } from 'src/api/defs/RemoveBoxSharingCmd.type'
import { SetInvocingDetailsCmd } from 'src/api/defs/SetInvocingDetailsCmd.type'
import { trimStringsDeep } from 'src/utils/trimStringsDeep'

type PayManuallyResponse = {
    url: string
}

export type ChangePasswordRequest = {
    newPassword: string
    oldPassword: string
}

const convertDateInMyContract = (mc: MyContract) => ({
    ...mc,
    createdAt: new Date(mc.createdAt),
    terminationAt: mc.terminationAt && new Date(mc.terminationAt)
})

const convertDateInMyRent = (r: MyRent) => ({
    ...r,
    paidAt: r.paidAt && new Date(r.paidAt)
})

export type MyRentFilter = AllNullable<ApiMyRentFilter>
export type MyContractFilter = AllNullable<ApiMyContractFilter>

export class MyZoneApi {
    constructor(private api: ApiClient) {}

    contracts = (filter: MyContractFilter): Promise<MyContract[]> =>
        this.api
            .do<MyContract[]>(apiOps.MyZone.contracts, {
                queryParams: filter
            })
            .then(items => items.map(convertDateInMyContract))

    contract = (id: number): Promise<MyContract> =>
        this.api
            .do<MyContract>(apiOps.MyZone.contractDetail, {
                pathParams: { id }
            })
            .then(convertDateInMyContract)

    contractFirstAt = (): Promise<MyContractFirstAt> =>
        this.api.do(apiOps.MyZone.contractFirstAt)

    profile = (): Promise<ClientUser> => this.api.do(apiOps.MyZone.getProfile)

    updateProfile = (req: ClientUser): Promise<ClientUser> => {
        const data = trimStringsDeep(req)
        return this.api
            .do(apiOps.MyZone.updateProfile, { data })
            .then(() => data)
    }

    setInvoicingDetails = (
        req: SetInvocingDetailsCmd
    ): Promise<SetInvocingDetailsCmd> => {
        const data = trimStringsDeep(req)
        return this.api
            .do(apiOps.MyZone.setInvoicingDetails, { data })
            .then(() => data)
    }

    rents = (filter: MyRentFilter): Promise<MyRent[]> =>
        this.api
            .do<MyRent[]>(apiOps.MyZone.rents, {
                queryParams: filter
            })
            .then(items => items.map(convertDateInMyRent))

    downloadPaymentInvoice = (id: number): Promise<DownloadLink> =>
        this.api.do<DownloadLink>(apiOps.MyZone.invoiceDownload, {
            pathParams: {
                id
            }
        })

    downloadLegalAgreement = (contractId: number): Promise<DownloadLink> =>
        this.api.do<DownloadLink>(apiOps.MyZone.legalAgreementDownload, {
            pathParams: {
                id: contractId
            }
        })

    contractRents = (id: number): Promise<MyRent[]> =>
        this.api
            .do<MyRent[]>(apiOps.MyZone.contractRents, {
                pathParams: { id }
            })
            .then(items => items.map(convertDateInMyRent))

    payFailedPaymentManually = (
        id: number,
        returnUrl: string
    ): Promise<string> =>
        this.api
            .do<PayManuallyResponse>(apiOps.ManualPayment.payManually, {
                pathParams: { id },
                data: {
                    returnUrl
                }
            })
            .then(r => r.url)

    changePassword = (
        req: ChangePasswordRequest
    ): Promise<ChangePasswordRequest> =>
        this.api.do<ChangePasswordRequest>(apiOps.MyZone.changePassword, {
            data: req
        })

    referralInfo = (): Promise<ClientReferralInfo> =>
        this.api.do(apiOps.MyZone.referralInfo)

    setLanguage = (req: SetLocaleCmd): Promise<SetLocaleCmd> =>
        this.api.do<SetLocaleCmd>(apiOps.MyZone.setLocale, {
            data: req
        })

    removeAccount = (): Promise<void> =>
        this.api.do(apiOps.MyZone.removeAccount)

    getCameraImage = (id: number): Promise<CaptureUrlResponse | Blob> =>
        this.api.do<Blob>(apiOps.ClientCamera.capturePicture, {
            data: { contractId: id },
            responseType: 'blob'
        })

    addBoxSharing = (req: AddBoxSharingCmd): Promise<void> =>
        this.api.do(apiOps.MyZone.addBoxSharing, {
            data: req
        })

    removeBoxSharing = (req: RemoveBoxSharingCmd): Promise<void> =>
        this.api.do(apiOps.MyZone.removeBoxSharing, {
            data: req
        })

    sharedBoxes = (): Promise<MySharedBox[]> =>
        this.api.do<MySharedBox[]>(apiOps.MyZone.sharedBoxes)
}
